import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin, Handshake, ShieldCheck, HeartHandshake, GraduationCap, Users,
  Phone, Mail, Clock, ArrowRight, Home as HomeIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { submitInquiry } from '../lib/api';
import SEO from '../components/SEO';
import { openInquiryPopup } from '../lib/contact';

const differences = [
  {
    title: 'Official presence',
    subtitle: 'in India and Russia',
    desc: '— giving you support both before departure and after arrival',
    icon: MapPin,
  },
  {
    title: 'Strong partnerships with top',
    subtitle: 'Russian universities',
    desc: '— recognized internationally',
    icon: Handshake,
  },
  {
    title: 'Transparent processes',
    subtitle: 'with no hidden fees',
    desc: '',
    icon: ShieldCheck,
  },
  {
    title: 'Comprehensive support',
    subtitle: '',
    desc: '— from admission to visa, accommodation, and post-arrival assistance',
    icon: HeartHandshake,
  },
  {
    title: 'Focus on student success',
    subtitle: 'and well-being',
    desc: '— including cultural adaptation and career guidance',
    icon: GraduationCap,
  },
];

export default function About() {
  const { toast } = useToast();
  const [f, setF] = useState({ name: '', phone: '', email: '', msg: '', consent: false });

  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.email) { toast({ title: 'Please fill Name, Phone & Email', variant: 'destructive' }); return; }
    if (!f.consent) { toast({ title: 'Please accept privacy policy', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      await submitInquiry({ type: 'contact', name: f.name, phone: f.phone, email: f.email, message: f.msg, consent: f.consent });
      toast({ title: 'Message Sent!', description: `Thank you ${f.name} — our team will get back to you within 24 hours.` });
      setF({ name: '', phone: '', email: '', msg: '', consent: false });
    } catch (err) {
      toast({ title: 'Could not send', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <SEO
        title="About Us — Russian Education | 35+ Years of MBBS Guidance"
        description="Since 1988, Russian Education has guided 1,200+ Indian students to MBBS in NMC-approved Russian universities. Offices in Noida & Udaipur. Vision, values & complete admission support."
        path="/about"
        keywords="Russian Education about us, MBBS consultancy India, MBBS in Russia consultants, Noida MBBS consultants"
      />
      {/* HERO — Blue → Red gradient banner */}
      <section className="relative overflow-hidden bg-blue-hero">
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' fill-opacity='0.15' d='M100 20c-8 10-4 22 6 26 8 4 20-2 22-12 2-9-4-18-14-18-4 0-9 1-14 4zm0 40c-15 10-30 30-30 55 0 20 15 40 30 40s30-20 30-40c0-25-15-45-30-55zm-40 90c-15 4-30 15-30 30 0 5 3 8 8 8h124c5 0 8-3 8-8 0-15-15-26-30-30-8-2-18-4-32-4-14 0-24 2-32 4-6 1-11 0-16 0z'/%3E%3C/svg%3E\")",
            backgroundSize: '160px',
          }} />

        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-14 md:py-20 relative grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div className="text-white">
            <div className="flex items-start gap-6 mb-6">
              <img src="/logo.jpg" alt="Russian Education" className="w-16 h-16 rounded-full border-2 border-white/40 bg-white p-1" />
              <div>
                <div className="text-xs md:text-sm uppercase tracking-widest text-white/80 font-semibold">your one stop gateway<br/>to Russian universities</div>
              </div>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1] tracking-tight">
              ABOUT RUSSIAN<br />EDUCATION
            </h1>
            <Button onClick={openInquiryPopup} data-testid="about-apply-hero" className="mt-8 bg-brand hover:bg-brand-dark text-white font-bold h-12 px-8 rounded-full btn-shift">
              Apply now
            </Button>
          </div>

          <div className="hidden md:block">
            <img
              src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=500&q=80"
              alt="Student"
              className="w-80 h-80 lg:w-96 lg:h-96 object-cover rounded-2xl shadow-2xl border-4 border-white/20"
            />
          </div>
        </div>
      </section>

      {/* Main statement */}
      <section className="py-14 md:py-20">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="flex items-start justify-between gap-6">
            <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl font-bold text-primary leading-tight max-w-4xl">
              Russian Education is an officially recognized organization that helps Indian students access high-quality higher education opportunities in Russia
            </h2>
            <div className="hidden md:flex flex-col gap-2 pt-2">
              <div className="w-14 h-14 rounded-full border-2 border-primary/20 shadow-md overflow-hidden bg-white flex items-center justify-center">
                <img src="https://flagcdn.com/w80/ru.png" alt="Russia" className="w-full h-full object-cover" />
              </div>
              <div className="w-14 h-14 rounded-full border-2 border-primary/20 shadow-md overflow-hidden bg-white flex items-center justify-center">
                <img src="https://flagcdn.com/w80/in.png" alt="India" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blue banner statement */}
      <section className="pb-10">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="relative bg-primary text-white rounded-2xl p-8 md:p-10 pr-16 md:pr-20 shadow-lg">
            <p className="text-base md:text-lg leading-relaxed max-w-5xl">
              Recently, Russian Education opened a dedicated branch in New Delhi, India, to strengthen education and
              cultural cooperation between Russia and India and to better serve Indian applicants who want to
              study abroad
            </p>
            <div className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/30 flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </section>

      {/* 2-column: text + team photo */}
      <section className="pb-14 md:pb-20">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-border p-7 shadow-sm flex flex-col">
            <p className="text-base text-foreground leading-relaxed">
              Russian Education works closely with leading Russian universities in Medicine, Engineering, and other fields
              to provide students with transparent, verified, and professional support throughout their study abroad
              journey.
            </p>
            <p className="mt-5 text-base text-foreground leading-relaxed">
              With offices both in Russia and India, Russian Education is uniquely positioned to assist students at
              every stage — from choosing the right program to settling into life in Russia.
            </p>
            <Button onClick={openInquiryPopup} data-testid="about-apply-side" className="mt-auto self-stretch bg-brand hover:bg-brand-dark text-white font-bold h-11 mt-6 rounded-lg btn-shift">
              Apply now
            </Button>
          </div>
          <div className="rounded-2xl overflow-hidden bg-muted">
            <img
              src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=900&q=80"
              alt="Team"
              className="w-full h-full object-cover min-h-[320px]"
            />
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="pb-14 md:pb-20">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-6">
          <div className="relative aspect-square md:aspect-auto min-h-[300px] rounded-2xl overflow-hidden bg-primary">
            <img
              src="https://images.unsplash.com/photo-1607435097405-db48f377bff6?w=800&q=80"
              alt="Target"
              className="w-full h-full object-cover mix-blend-overlay opacity-70"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-40 h-40 md:w-56 md:h-56 rounded-full bg-gradient-to-br from-brand via-white to-primary flex items-center justify-center shadow-2xl">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-white flex items-center justify-center">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand" />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-border p-7 md:p-9 shadow-sm flex flex-col justify-center">
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-5">Our Mission</h3>
            <p className="text-base text-foreground leading-relaxed">
              is to make international education accessible, safe, and rewarding for students from India. We aim
              to build strong academic pathways, support cultural exchange, and help you unlock global career
              opportunities through higher education in Russia.
            </p>
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="pb-14 md:pb-20">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-6">
          <div className="bg-primary text-white rounded-2xl p-7 md:p-9 shadow-lg flex flex-col justify-center order-2 md:order-1">
            <h3 className="font-serif text-3xl md:text-4xl font-bold mb-5">Our Vision</h3>
            <p className="text-base text-white/90 leading-relaxed">
              To become India&apos;s most trusted bridge to Russian medical and technical education — empowering
              every deserving Indian student with world-class academic opportunities, regardless of their
              economic background or geographic location.
            </p>
            <p className="mt-4 text-base text-white/90 leading-relaxed">
              We envision a future where thousands of Indian doctors and engineers, educated in Russia, return
              home to serve their communities with excellence, integrity, and global perspective.
            </p>
          </div>
          <div className="relative min-h-[300px] rounded-2xl overflow-hidden bg-muted order-1 md:order-2">
            <img
              src="https://images.unsplash.com/photo-1509062522246-3755977927d7?w=900&q=80"
              alt="Vision"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/40 to-transparent" />
          </div>
        </div>
      </section>

      {/* Our Goals */}
      <section className="pb-14 md:pb-20">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="max-w-3xl mb-10">
            <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary leading-tight">Our Goals</h3>
            <p className="mt-3 text-muted-foreground text-base md:text-lg">Six clear commitments that guide every student we serve.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { n: 1, t: 'Guide 10,000+ students', d: 'to top Russian government universities over the next decade — with zero broker involvement.' },
              { n: 2, t: 'Maintain 100% transparency', d: 'in every fee, document, and admission step — no hidden charges, ever.' },
              { n: 3, t: 'Build strong alumni networks', d: 'so every new student arrives in Russia to a warm Indian community and mentorship.' },
              { n: 4, t: 'Support FMGE / NExT success', d: 'through partnerships with leading coaching institutes for our alumni.' },
              { n: 5, t: 'Expand partnerships', d: 'with more Russian universities to offer wider choice, better fees, and superior facilities.' },
              { n: 6, t: 'Empower parents', d: 'with clarity and confidence — because your family&apos;s trust is the foundation of everything we do.' },
            ].map((g) => (
              <div key={g.n} className="relative bg-white rounded-2xl border border-border p-6 shadow-sm card-lift">
                <div className="absolute -top-3 right-4 w-8 h-8 rounded-full bg-brand text-white text-sm font-bold flex items-center justify-center shadow-lg shadow-brand/40">{g.n}</div>
                <h4 className="font-serif text-lg font-bold text-primary leading-tight">{g.t}</h4>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{g.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-14 md:py-20 bg-muted/50">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <h3 className="font-serif text-3xl md:text-5xl font-bold text-primary mb-10">What Makes Us Different</h3>

          <div className="grid md:grid-cols-3 gap-4">
            {differences.slice(0, 3).map((d, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden bg-primary text-white p-7 min-h-[220px] flex flex-col justify-between shadow-lg">
                <div className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath fill='%23fff' d='M50 10c-4 8-2 14 4 16 5 2 12-1 13-7 1-6-4-11-9-11-3 0-6 1-8 2zm0 25c-10 6-19 20-19 34 0 12 9 24 19 24s19-12 19-24c0-14-9-28-19-34z'/%3E%3C/svg%3E\")",
                    backgroundSize: '80px',
                  }} />
                <div className="relative">
                  <h4 className="font-serif text-xl md:text-2xl font-bold leading-tight">
                    {d.title}
                    {d.subtitle && <><br/>{d.subtitle}</>}
                  </h4>
                  {d.desc && <p className="mt-4 text-sm text-white/90 leading-relaxed">{d.desc}</p>}
                </div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {differences.slice(3).map((d, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden bg-primary text-white p-7 min-h-[180px] shadow-lg">
                <div className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath fill='%23fff' d='M50 10c-4 8-2 14 4 16 5 2 12-1 13-7 1-6-4-11-9-11-3 0-6 1-8 2zm0 25c-10 6-19 20-19 34 0 12 9 24 19 24s19-12 19-24c0-14-9-28-19-34z'/%3E%3C/svg%3E\")",
                    backgroundSize: '80px',
                  }} />
                <div className="relative">
                  <h4 className="font-serif text-xl md:text-2xl font-bold leading-tight">
                    {d.title}
                    {d.subtitle && <><br/>{d.subtitle}</>}
                  </h4>
                  {d.desc && <p className="mt-4 text-sm text-white/90 leading-relaxed">{d.desc}</p>}
                </div>
              </div>
            ))}
          </div>

          {/* Dark quote box */}
          <div className="mt-10 bg-[#0a0a0a] text-white rounded-2xl p-6 md:p-8">
            <p className="text-sm md:text-base leading-relaxed">
              At the Russian Education, we are more than consultants — we are your trusted partners on the path
              to a successful international education. Your ambitions matter, and we&apos;re here to help you achieve
              them with confidence and clarity
            </p>
          </div>
        </div>
      </section>

      {/* Contact Us section */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-6">
          {/* Left: Contact form */}
          <div className="bg-primary text-white rounded-2xl p-7 md:p-9">
            <h3 className="font-serif text-3xl md:text-4xl font-bold mb-3">Contact us</h3>
            <p className="text-sm text-white/85 leading-relaxed mb-6">
              We are always here to help you take the first step toward your international education in Russia.
            </p>

            <form onSubmit={submit} className="space-y-3">
              <textarea
                value={f.msg} onChange={(e) => setF({ ...f, msg: e.target.value })}
                placeholder="Send a message"
                rows={3}
                className="w-full px-3 py-2.5 rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
              />
              <div className="grid grid-cols-3 gap-2">
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name"
                  className="h-10 px-3 rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
                <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} type="tel" placeholder="Telephone"
                  className="h-10 px-3 rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
                <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} type="email" placeholder="Email"
                  className="h-10 px-3 rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
              </div>

              <Button type="submit" className="w-full bg-black hover:bg-black/80 text-white h-11 rounded-lg font-bold">
                Apply now
              </Button>

              <label className="flex items-start gap-2 text-xs text-white/80 pt-2 cursor-pointer">
                <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })}
                  className="mt-0.5 accent-brand" />
                <span>I consent to the processing of personal data and receiving newsletters from Russian Education, I agree with the privacy policy</span>
              </label>
            </form>
          </div>

          {/* Right: Contact info */}
          <div className="bg-brand text-white rounded-2xl p-7 md:p-9">
            <div className="space-y-5">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Email:</div>
                <a href="mailto:admissions@russianeducation.in" className="mt-1 block text-lg font-semibold hover:underline">
                  admissions@russianeducation.in
                </a>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Phone:</div>
                  <a href="tel:+919509406802" className="mt-1 block text-sm font-bold hover:underline">+91 95094 06802</a>
                  <div className="mt-2 text-xs text-white/85">Also on WhatsApp</div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Working Hours:</div>
                  <div className="mt-1 text-xs">Monday to Friday: 10:00 AM – 7:00 PM</div>
                  <div className="mt-1 text-xs">Saturday: By appointment only</div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Head Office:</div>
                <div className="mt-1 text-sm font-semibold">Russian Education</div>
                <div className="text-xs text-white/85 leading-relaxed">
                  Alex Hospital, Shiv Gate,<br/>
                  Sector 115, Noida
                </div>
                <div className="mt-3 text-xs uppercase tracking-widest text-white/75 font-semibold">Corporate Office:</div>
                <div className="mt-1 text-xs text-white/85 leading-relaxed">
                  God Gift Complex, Hiran Magri,<br/>
                  Sector 3, Udaipur
                </div>
              </div>

              {/* Map placeholder */}
              <div className="rounded-lg overflow-hidden border border-white/20">
                <iframe
                  title="office-location"
                  src="https://maps.google.com/maps?q=Sector%20115%20Noida&t=&z=13&ie=UTF8&iwloc=&output=embed"
                  width="100%" height="180" style={{ border: 0 }} loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom copyright */}
      <div className="py-4 bg-white border-t border-border">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <div>© 1988 — 2026, Russian Education</div>
          <div>All rights reserved</div>
        </div>
      </div>
    </div>
  );
}
